import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Settings, Save, Loader2, RefreshCw, Plus, Trash2, Printer, Download,
  Calendar, Percent, Building2, ChevronDown, Receipt, Eye, EyeOff,
} from "lucide-react";
import {
  listFranchiseSettings, saveFranchiseSettings,
  previewNetSales, listRoyaltyInvoices, createRoyaltyInvoice,
  updateRoyaltyInvoiceStatus, deleteRoyaltyInvoice, fetchRoyaltyInvoicePdfBlob,
  createFranchise, updateFranchiseColor, fetchSpokes,
} from "@/lib/api";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const fmt = (sym, n) => `${sym}${Number(n || 0).toFixed(2)}`;

const ROYALTY_STATUS_OPTIONS = [
  { value: "draft", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
];
const statusLabel = (s) =>
  ROYALTY_STATUS_OPTIONS.find((o) => o.value === s)?.label || "Pending";
const statusBadgeClass = (s) => {
  switch (s) {
    case "paid": return "bg-dotr-primary text-white";
    case "sent": return "bg-dotr-ink text-white";
    case "overdue": return "bg-red-600 text-white";
    case "draft":
    default: return "bg-dotr-yellow text-dotr-ink";
  }
};

// Shared swatch palette for picking franchise brand colours. Admin can also
// paste any hex via the inline text input.
const COLOR_SWATCHES = [
  "#DF513B", "#E8B241", "#2D5C8F", "#4F7355", "#7E3C8A", "#C44A6A",
  "#F39C12", "#1E6FBF", "#16A085", "#D35400", "#8E44AD", "#2C3E50",
];

const ColorPickerField = ({ value, onChange, testIdBase }) => {
  const safe = /^#[0-9A-Fa-f]{6}$/.test(value || "") ? value : "#3A3F4B";
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Brand colour</div>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="color"
          value={safe}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="w-10 h-10 border-2 border-dotr-ink cursor-pointer"
          data-testid={`${testIdBase}-native`}
          aria-label="Pick colour"
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          placeholder="#RRGGBB"
          maxLength={7}
          className="brutal-input h-10 w-28 px-2 bg-white font-mono text-sm"
          data-testid={`${testIdBase}-hex`}
        />
        <div className="flex gap-1 flex-wrap">
          {COLOR_SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange(c)}
              className={`w-8 h-8 border-2 border-dotr-ink ${value?.toUpperCase() === c ? "ring-2 ring-offset-2 ring-dotr-ink" : ""}`}
              style={{ background: c }}
              aria-label={`Pick ${c}`}
              data-testid={`${testIdBase}-swatch-${c.replace("#","")}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------- Franchise Fee Editor (single franchise) ----------
const FranchiseEditor = ({ franchise, spokeMeta, onSaved }) => {
  const [form, setForm] = useState(franchise);
  const [saving, setSaving] = useState(false);
  const [savingColor, setSavingColor] = useState(false);
  const [color, setColor] = useState(spokeMeta?.color || "#3A3F4B");

  useEffect(() => { setForm(franchise); }, [franchise]);
  useEffect(() => { setColor(spokeMeta?.color || "#3A3F4B"); }, [spokeMeta?.color]);

  const change = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    const royalty = Number(form.royalty_pct);
    const tech = Number(form.tech_support_fee);
    const mkt = Number(form.marketing_levy);
    if (Number.isNaN(royalty) || royalty < 0 || royalty > 100) return toast.error("Royalty % must be 0–100");
    if (Number.isNaN(tech) || tech < 0) return toast.error("Technology & support fee must be ≥ 0");
    if (Number.isNaN(mkt) || mkt < 0) return toast.error("Marketing levy must be ≥ 0");
    setSaving(true);
    try {
      await saveFranchiseSettings(form.spoke, {
        royalty_pct: royalty,
        tech_support_fee: tech,
        marketing_levy: mkt,
        vat_applied: !!form.vat_applied,
      });
      toast.success(`${form.spoke} franchise settings saved`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const saveColor = async () => {
    if (!/^#[0-9A-Fa-f]{6}$/.test(color)) return toast.error("Enter a 6-digit hex colour (e.g. #1E6FBF)");
    if (color === spokeMeta?.color) return;
    setSavingColor(true);
    try {
      await updateFranchiseColor(form.spoke, color);
      toast.success(`${form.spoke} colour updated`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Colour save failed");
    } finally { setSavingColor(false); }
  };

  return (
    <div className="brutal-card p-5 md:p-6" data-testid={`franchise-card-${form.spoke}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 border-2 border-dotr-ink text-white flex items-center justify-center"
            style={{ background: spokeMeta?.color || "#DF513B", color: spokeMeta?.fg || "#FFFFFF" }}
            data-testid={`franchise-swatch-${form.spoke}`}
          >
            <Building2 size={20} />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-xl md:text-2xl uppercase tracking-tight">{form.spoke} Franchise</h3>
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted">DUM ON THE RUN<sup>®</sup> · Royalty terms</p>
          </div>
        </div>
      </div>

      {/* Brand colour row */}
      <div className="mb-4 flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[320px]">
          <ColorPickerField
            value={color}
            onChange={setColor}
            testIdBase={`franchise-color-${form.spoke}`}
          />
        </div>
        <button
          type="button"
          onClick={saveColor}
          disabled={savingColor}
          data-testid={`franchise-color-save-${form.spoke}`}
          className="brutal-btn-ghost px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          {savingColor ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Save colour
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Royalty % of Net Sales</div>
          <div className="relative">
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.royalty_pct ?? ""}
              onChange={change("royalty_pct")}
              data-testid={`franchise-royalty-${form.spoke}`}
              className="brutal-input w-full h-11 pl-3 pr-9 bg-white"
            />
            <Percent size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-dotr-inkmuted" />
          </div>
        </label>
        <label className="block">
          <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Technology &amp; Support (£/mo)</div>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.tech_support_fee ?? ""}
            onChange={change("tech_support_fee")}
            data-testid={`franchise-tech-${form.spoke}`}
            className="brutal-input w-full h-11 px-3 bg-white"
          />
        </label>
        <label className="block">
          <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Marketing Levy (£/mo)</div>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.marketing_levy ?? ""}
            onChange={change("marketing_levy")}
            data-testid={`franchise-mkt-${form.spoke}`}
            className="brutal-input w-full h-11 px-3 bg-white"
          />
        </label>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!form.vat_applied}
            onChange={change("vat_applied")}
            data-testid={`franchise-vat-${form.spoke}`}
            className="w-4 h-4 border-2 border-dotr-ink accent-dotr-primary"
          />
          <span className="font-mono text-[11px] uppercase tracking-widest text-dotr-ink">Apply 20% VAT on royalty invoices</span>
        </label>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={`franchise-save-${form.spoke}`}
          className="brutal-btn px-5 py-2.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </button>
      </div>

      {form.updated_at && (
        <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">
          Last updated {new Date(form.updated_at).toLocaleString()} by {form.updated_by || "—"}
        </div>
      )}
    </div>
  );
};

// ---------- Royalty Invoice Generator ----------
const NewRoyaltyInvoiceModal = ({ franchises, onClose, onCreated }) => {
  const now = new Date();
  const [spoke, setSpoke] = useState(franchises[0]?.spoke || "");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [preview, setPreview] = useState(null);
  const [override, setOverride] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [notes, setNotes] = useState("");

  const loadPreview = useCallback(async () => {
    if (!spoke) return;
    setLoading(true);
    try {
      const d = await previewNetSales(spoke, year, month);
      setPreview(d);
      setOverride(String(d.net_sales));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  }, [spoke, year, month]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const computed = useMemo(() => {
    if (!preview) return null;
    const net = Number(override) || 0;
    const royalty = +(net * (preview.royalty_pct / 100.0)).toFixed(2);
    const subtotal = +(royalty + preview.tech_support_fee + preview.marketing_levy).toFixed(2);
    const vat = preview.vat_applied ? +(subtotal * 0.20).toFixed(2) : 0;
    const total = +(subtotal + vat).toFixed(2);
    return { net, royalty, subtotal, vat, total };
  }, [preview, override]);

  const create = async () => {
    setCreating(true);
    try {
      await createRoyaltyInvoice({
        spoke,
        period_year: Number(year),
        period_month: Number(month),
        net_sales_override: computed?.net ?? null,
        notes: notes || null,
      });
      toast.success("Royalty invoice created");
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y - 1, y, y + 1];
  }, [now]);

  return (
    <div className="fixed inset-0 z-50 bg-dotr-ink/60 flex items-center justify-center p-4" data-testid="royalty-create-modal">
      <div className="bg-dotr-bg border-2 border-dotr-ink max-w-xl w-full shadow-brutal">
        <div className="flex items-center justify-between border-b-2 border-dotr-ink px-5 py-4">
          <div className="flex items-center gap-2">
            <Receipt size={18} />
            <h3 className="font-heading uppercase tracking-tight text-lg">Generate Royalty Invoice</h3>
          </div>
          <button onClick={onClose} data-testid="royalty-modal-close" className="w-8 h-8 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center" aria-label="Close">×</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Franchise</div>
              <select value={spoke} onChange={(e) => setSpoke(e.target.value)} data-testid="royalty-spoke" className="brutal-input w-full h-10 px-3 bg-white">
                {franchises.map((f) => <option key={f.spoke} value={f.spoke}>{f.spoke}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Month</div>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="royalty-month" className="brutal-input w-full h-10 px-3 bg-white">
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Year</div>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="royalty-year" className="brutal-input w-full h-10 px-3 bg-white">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          </div>

          <div className="brutal-card bg-dotr-muted/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">Auto-calculated Net Sales from orders</div>
              <button type="button" onClick={loadPreview} className="w-8 h-8 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center" title="Refresh" aria-label="Refresh">
                {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              </button>
            </div>
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Net Sales (£) — editable</div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                data-testid="royalty-net-sales"
                className="brutal-input w-full h-11 px-3 bg-white"
              />
            </label>
            {preview && computed && (
              <div className="mt-3 border-t-2 border-dotr-ink/20 pt-3 space-y-1 font-mono text-xs">
                <div className="flex justify-between"><span>Royalty ({preview.royalty_pct.toFixed(2)}%)</span><span>{fmt("£", computed.royalty)}</span></div>
                <div className="flex justify-between"><span>Technology &amp; Support</span><span>{fmt("£", preview.tech_support_fee)}</span></div>
                <div className="flex justify-between"><span>Marketing Levy</span><span>{fmt("£", preview.marketing_levy)}</span></div>
                <div className="flex justify-between border-t border-dotr-ink/40 pt-1 mt-1"><span>Subtotal</span><span>{fmt("£", computed.subtotal)}</span></div>
                {preview.vat_applied && <div className="flex justify-between"><span>VAT (20%)</span><span>{fmt("£", computed.vat)}</span></div>}
                <div className="flex justify-between font-heading text-base pt-1"><span>Total</span><span data-testid="royalty-preview-total">{fmt("£", computed.total)}</span></div>
              </div>
            )}
          </div>

          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Notes (optional)</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="royalty-notes"
              rows={2}
              className="brutal-input w-full px-3 py-2 bg-white"
            />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 border-t-2 border-dotr-ink p-4">
          <button onClick={onClose} className="brutal-btn-ghost px-4 py-2 text-sm">Cancel</button>
          <button
            onClick={create}
            disabled={creating || !spoke}
            data-testid="royalty-create-btn"
            className="brutal-btn px-5 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Create invoice
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Add Franchise Modal ----------
const AddFranchiseModal = ({ existingSpokes, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: "",
    royalty_pct: 8,
    tech_support_fee: 50,
    marketing_levy: 100,
    vat_applied: false,
    password: "",
    color: "#1E6FBF",
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const change = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const previewUsername = (form.name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/-/g, "");
  const nameClash = previewUsername && existingSpokes.some(
    (s) => s.toLowerCase().replace(/\s+/g, "") === previewUsername
  );

  const create = async () => {
    const n = (form.name || "").trim();
    if (n.length < 2) return toast.error("Enter a franchise name (min 2 chars)");
    if (!/^[A-Za-z][A-Za-z0-9 \-]*[A-Za-z0-9]$/.test(n)) {
      return toast.error("Name may use letters, digits, spaces and hyphens only");
    }
    if (!form.password || form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (Number(form.royalty_pct) < 0 || Number(form.royalty_pct) > 100) return toast.error("Royalty must be 0–100%");
    setSaving(true);
    try {
      const r = await createFranchise({
        name: n,
        royalty_pct: Number(form.royalty_pct),
        tech_support_fee: Number(form.tech_support_fee),
        marketing_levy: Number(form.marketing_levy),
        vat_applied: !!form.vat_applied,
        password: form.password,
        color: form.color,
      });
      toast.success(`${r.spoke} franchise added — login: ${r.username}`);
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-dotr-ink/60 flex items-center justify-center p-4" data-testid="add-franchise-modal">
      <div className="bg-dotr-bg border-2 border-dotr-ink max-w-xl w-full shadow-brutal">
        <div className="flex items-center justify-between border-b-2 border-dotr-ink px-5 py-4">
          <div className="flex items-center gap-2">
            <Building2 size={18} />
            <h3 className="font-heading uppercase tracking-tight text-lg">Add Franchise Spoke</h3>
          </div>
          <button onClick={onClose} data-testid="add-franchise-close" className="w-8 h-8 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center" aria-label="Close">×</button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Franchise name</div>
            <input
              type="text"
              value={form.name}
              onChange={change("name")}
              placeholder="e.g. Reading"
              data-testid="add-franchise-name"
              className="brutal-input w-full h-11 px-3 bg-white"
              maxLength={40}
            />
            {previewUsername && (
              <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">
                Login username will be: <span className="text-dotr-ink font-bold">{previewUsername}</span>
                {nameClash && <span className="ml-2 text-dotr-primary">⚠ name already in use</span>}
              </div>
            )}
          </label>

          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Royalty %</div>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.royalty_pct}
                  onChange={change("royalty_pct")}
                  data-testid="add-franchise-royalty"
                  className="brutal-input w-full h-11 pl-3 pr-9 bg-white"
                />
                <Percent size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-dotr-inkmuted" />
              </div>
            </label>
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Tech &amp; Support £/mo</div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.tech_support_fee}
                onChange={change("tech_support_fee")}
                data-testid="add-franchise-tech"
                className="brutal-input w-full h-11 px-3 bg-white"
              />
            </label>
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Marketing £/mo</div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.marketing_levy}
                onChange={change("marketing_levy")}
                data-testid="add-franchise-mkt"
                className="brutal-input w-full h-11 px-3 bg-white"
              />
            </label>
          </div>

          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!form.vat_applied}
              onChange={change("vat_applied")}
              data-testid="add-franchise-vat"
              className="w-4 h-4 border-2 border-dotr-ink accent-dotr-primary"
            />
            <span className="font-mono text-[11px] uppercase tracking-widest text-dotr-ink">Apply 20% VAT on royalty invoices</span>
          </label>

          <ColorPickerField
            value={form.color}
            onChange={(c) => setForm((f) => ({ ...f, color: c }))}
            testIdBase="add-franchise-color"
          />

          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted mb-1">Spoke password (admin sets this)</div>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={change("password")}
                data-testid="add-franchise-password"
                className="brutal-input w-full h-11 pl-3 pr-10 bg-white font-mono"
                minLength={6}
                maxLength={100}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 border-2 border-dotr-ink bg-white flex items-center justify-center"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
            </div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">
              Share this with the franchisee so they can sign in as {previewUsername || "<username>"}.
            </div>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 border-t-2 border-dotr-ink p-4">
          <button onClick={onClose} className="brutal-btn-ghost px-4 py-2 text-sm">Cancel</button>
          <button
            onClick={create}
            disabled={saving}
            data-testid="add-franchise-create"
            className="brutal-btn px-5 py-2 text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Create franchise
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Main page ----------
export const FranchiseSettings = () => {
  const [franchises, setFranchises] = useState([]);
  const [spokeMetaMap, setSpokeMetaMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sp] = await Promise.all([listFranchiseSettings(), fetchSpokes()]);
      setFranchises(s.franchises || []);
      const map = {};
      for (const row of (sp.spokes || [])) map[row.name] = row;
      setSpokeMetaMap(map);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="page-franchise" className="space-y-5">
      <div className="brutal-card p-5 md:p-6">
        <div className="flex items-start md:items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 border-2 border-dotr-ink bg-dotr-ink text-white flex items-center justify-center"><Settings size={20} /></div>
            <div>
              <h2 className="font-heading font-semibold text-2xl md:text-3xl uppercase tracking-tight">Franchise Settings</h2>
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted">Royalty terms · Monthly fees · Brand colour</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setShowAdd(true)} data-testid="btn-add-franchise"
              className="brutal-btn-ghost px-4 py-2.5 text-xs inline-flex items-center gap-1.5">
              <Building2 size={12} /> Add franchise
            </button>
            <button onClick={load} disabled={loading} data-testid="btn-refresh-franchise" className="brutal-btn-ghost px-3 py-2.5 text-xs inline-flex items-center gap-1.5">
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            </button>
          </div>
        </div>
      </div>

      {loading && franchises.length === 0 ? (
        <div className="brutal-card p-16 flex items-center justify-center"><Loader2 size={24} className="animate-spin" /></div>
      ) : franchises.length === 0 ? (
        <div className="brutal-card p-16 text-center" data-testid="franchise-empty">
          <Building2 size={30} className="mx-auto mb-3 text-dotr-inkmuted" />
          <div className="font-heading text-xl uppercase tracking-tight mb-1">No franchise spokes configured</div>
        </div>
      ) : (
        <div className="space-y-4">
          {franchises.map((f) => (
            <FranchiseEditor key={f.spoke} franchise={f} spokeMeta={spokeMetaMap[f.spoke]} onSaved={load} />
          ))}
        </div>
      )}

      {showAdd && (
        <AddFranchiseModal
          existingSpokes={franchises.map((f) => f.spoke)}
          onClose={() => setShowAdd(false)}
          onCreated={load}
        />
      )}
    </div>
  );
};

export default FranchiseSettings;
